import {
  AloftPrice,
  expected,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftPrice,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { AlertDefinition } from '../../types.ts';
import {
  getCompTypeComparableLabel,
  getCompTypeLabel,
  getScopedAlertType,
} from '../utils/compTypeScoping.ts';

export const bracketedMarketValue: AlertDefinition = ({
  compAdjustments,
  compSequence,
  compType,
  order,
  reconciliation,
}) => {
  if (!order.isInspectifyOrder) return undefined;
  const compAdjustedSalePrices = compSequence
    .map((id) => compAdjustments[id]?.adjustedSalePrice.adjustment)
    .filter(isDefined);

  let opinionOfValue: AloftPrice | undefined;
  let valueLabel: string;
  let propertyID: 'subject' | 'subjectArv';
  switch (compType) {
    case 'arv':
      opinionOfValue = orUndefined(schAloftPrice.parse)(reconciliation.opinionOfAfterRepairValue);
      valueLabel = `${getCompTypeComparableLabel(compType)} Value`;
      propertyID = 'subjectArv';
      break;
    case 'aiv':
      opinionOfValue = orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue);
      valueLabel = `${getCompTypeComparableLabel(compType)} Value`;
      propertyID = 'subject';
      break;
    default:
      opinionOfValue = orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue);
      valueLabel = 'Market Value';
      propertyID = 'subject';
      break;
  }

  if (!opinionOfValue || compAdjustedSalePrices.length < 2) return undefined;

  const minAdjustedSalePrice = expected(AloftPrice.min(compAdjustedSalePrices));
  const maxAdjustedSalePrice = expected(AloftPrice.max(compAdjustedSalePrices));

  if (opinionOfValue.ge(minAdjustedSalePrice) && opinionOfValue.le(maxAdjustedSalePrice)) {
    return undefined;
  }

  let compsLabel: string;
  switch (compType) {
    case 'aiv':
    case 'arv':
      compsLabel = `${getCompTypeLabel(compType)} Comparables`;
      break;
    default:
      compsLabel = 'comparables';
      break;
  }

  return {
    type: getScopedAlertType(schAlertType.enum.bracketedMarketValue, compType),
    status: 'active',
    message: `Opinion of ${valueLabel} (${opinionOfValue.toString()}) is not bracketed by the adjusted sales price range of ${compsLabel} (${minAdjustedSalePrice.toString()} - ${maxAdjustedSalePrice.toString()}).`,
    propertyID,
    ...(compType ? { compType } : {}),
    severity: 'error',
    fixingInfo: {
      tips: 'Tips: Bracketing is a key appraisal practice used to demonstrate support for the final value conclusion and ensure consistency with the comparable sales analysis. Revise the Opinion of Market Value so that it is bracketed, or provide clear commentary supporting why the value falls outside of that range',
      pairedCostRouteItem: schPairedCostRouteItem.enum['adjustments-grid'],
      fixingMethod: ['comment', 'action'],
      commentAlwaysRequired: false,
    },
    triggers: [],
  };
};
